import { create } from 'zustand';
import { generateId } from '@/lib/utils';

export type NotificationItemType = 'info' | 'success' | 'warning' | 'error';

export interface NotificationItem {
  id: string;
  type: NotificationItemType;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  link?: string;
  /** Actions intégrées (ex. demande de déblocage formation). */
  actionType?: 'unblock_request';
  actionPayload?: {
    requestId: string;
    flowTitle?: string;
    requesterName?: string;
    focusLossCount?: number;
  };
}

interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
  isDropdownOpen: boolean;
  isLoading: boolean;

  addNotification: (
    notification: Omit<NotificationItem, 'read' | 'createdAt'> & { id?: string },
  ) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  toggleDropdown: () => void;
  setDropdownOpen: (open: boolean) => void;
  fetchNotifications: () => Promise<void>;
}

function computeUnread(notifications: NotificationItem[]): number {
  return notifications.filter((n) => !n.read).length;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isDropdownOpen: false,
  isLoading: false,

  addNotification: (notification) =>
    set((s) => {
      const stableId = notification.id ?? generateId();
      if (s.notifications.some((n) => n.id === stableId)) {
        return s;
      }
      const { id: _id, ...rest } = notification;
      const item: NotificationItem = {
        ...rest,
        id: stableId,
        read: false,
        createdAt: new Date().toISOString(),
      };
      const notifications = [item, ...s.notifications];
      return { notifications, unreadCount: computeUnread(notifications) };
    }),

  markAsRead: (id) =>
    set((s) => {
      const notifications = s.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      );
      return { notifications, unreadCount: computeUnread(notifications) };
    }),

  markAllAsRead: () =>
    set((s) => {
      const notifications = s.notifications.map((n) => ({ ...n, read: true }));
      return { notifications, unreadCount: 0 };
    }),

  removeNotification: (id) =>
    set((s) => {
      const notifications = s.notifications.filter((n) => n.id !== id);
      return { notifications, unreadCount: computeUnread(notifications) };
    }),

  toggleDropdown: () => set((s) => ({ isDropdownOpen: !s.isDropdownOpen })),
  setDropdownOpen: (open) => set({ isDropdownOpen: open }),

  fetchNotifications: async () => {
    // Legacy no-op: notifications are currently sourced client-side.
    set({ isLoading: false });
  },
}));
